import { useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  CircleCheckmarkIcon,
  CircleEllipsisIcon,
  CircleEmptyIcon,
  CircleMinusIcon,
  CirclePauseIcon,
  FormLabel,
  TableContainer,
  TableV2,
  Tbody,
  Td,
  Tooltip,
  Tr,
} from '@frontend/components';
import { useHasVerticalOverflow } from '@frontend/utils';

import {
  WORKFLOW_OR_QUOTE_ITEM_STATUS,
  WORKFLOW_STEP_STATUS,
  WORKFLOW_STEP_STATUS_LABEL,
} from 'constants/all';

import { updateQuoteItemWorkflowStep } from 'modules/quotes/quoteItems/data/actions';
import { getWorkflowStepsByQuoteItemId } from 'modules/quotes/quoteItems/data/attributeSelectors/quoteItems';
import {
  getIsQuoteItemReadOnly,
  getQuoteWorkflowSteps,
} from 'modules/quotes/quoteItems/data/selectors';

import StatusSelector from './StatusSelector';
import StatusSelectorOptionLabel from './StatusSelectorOptionLabel';

export const statusesToIcon: Record<string, JSX.Element> = {
  [WORKFLOW_OR_QUOTE_ITEM_STATUS.NOT_STARTED]: (
    <CircleMinusIcon boxSize={2.5} color="status.notStarted" />
  ),
  [WORKFLOW_OR_QUOTE_ITEM_STATUS.IN_PROGRESS]: (
    <CircleEllipsisIcon boxSize={2.5} color="status.inProgress" />
  ),
  [WORKFLOW_OR_QUOTE_ITEM_STATUS.ON_HOLD]: (
    <CirclePauseIcon boxSize={2.5} color="status.onHold" />
  ),
  [WORKFLOW_OR_QUOTE_ITEM_STATUS.COMPLETED]: (
    <CircleCheckmarkIcon boxSize={2.5} color="status.complete" />
  ),
  [WORKFLOW_OR_QUOTE_ITEM_STATUS.NOT_APPLICABLE]: (
    <CircleEmptyIcon boxSize={2.5} color="status.notApplicable" />
  ),
  [WORKFLOW_OR_QUOTE_ITEM_STATUS.NO_QUOTE]: (
    <CircleEmptyIcon boxSize={2.5} color="status.notApplicable" />
  ),
};

type WorkflowStatusOption = {
  label: JSX.Element;
  value: string;
};

type WorkflowStatusOptionGroup = {
  options: WorkflowStatusOption[];
};

interface WorkflowStepRowProps {
  label: string;
  workStepId: number | null;
  status: string | undefined;
  isQuoteItemReadOnly: boolean;
  workflowStatusOptions: WorkflowStatusOptionGroup[];
  quoteItemId: number;
  isFirstItemInTable?: boolean;
  isLastItemInTable?: boolean;
}

const WorkflowStepRow = ({
  label,
  workStepId,
  status,
  isQuoteItemReadOnly,
  workflowStatusOptions,
  quoteItemId,
  isFirstItemInTable = false,
  isLastItemInTable = false,
}: WorkflowStepRowProps) => {
  const dispatch = useDispatch();
  const labelRef = useRef<HTMLLabelElement>(null);
  const isLabelOverflowing = useHasVerticalOverflow(labelRef);

  const handleChange = useCallback(
    (value: string) => {
      dispatch(
        updateQuoteItemWorkflowStep.request({
          name: label,
          quoteItemId,
          status: value,
          workStepId,
        }),
      );
    },
    [dispatch, label, quoteItemId, workStepId],
  );

  return (
    <Tr>
      <Td width={27} px={0.75} py={0} h={4} bg="page.plateLight">
        <Tooltip
          label={label}
          placement="right"
          isDisabled={!isLabelOverflowing}
        >
          <FormLabel
            ref={labelRef}
            mb={0}
            height="auto"
            noOfLines={2}
            wordBreak="break-word"
          >
            {label}
          </FormLabel>
        </Tooltip>
      </Td>
      <Td p={0} h={4} bg="page.surface" overflow="visible">
        <StatusSelector
          isReadOnly={isQuoteItemReadOnly}
          label={label}
          onChange={handleChange}
          currentStatus={status ?? WORKFLOW_STEP_STATUS.NOT_STARTED}
          statusOptions={workflowStatusOptions}
          isSelectorInTable
          isFirstItemInTable={isFirstItemInTable}
          isLastItemInTable={isLastItemInTable}
        />
      </Td>
    </Tr>
  );
};

interface CustomWorkflowStepsProps {
  quoteItemId: number;
}

const CustomWorkflowSteps = ({ quoteItemId }: CustomWorkflowStepsProps) => {
  const isQuoteItemReadOnly = useSelector((state) =>
    getIsQuoteItemReadOnly(state, quoteItemId),
  );
  const quoteItemWorkflowSteps = useSelector((state) =>
    getWorkflowStepsByQuoteItemId(state, quoteItemId),
  );
  const quoteWorkflowSteps = useSelector(getQuoteWorkflowSteps);

  const memoizedNotIncludedSteps = useMemo(() => {
    /*
    Get steps that exist in the quote item but do not exist in the current quote workflow.

    These can be steps that used to exist or were one-offs created manually or by p3l.
    */
    return Object.keys(quoteItemWorkflowSteps).filter(
      (quoteItemStep) =>
        !quoteWorkflowSteps.some(
          (workflowStep) => workflowStep?.step?.locator === quoteItemStep,
        ),
    );
  }, [quoteItemWorkflowSteps, quoteWorkflowSteps]);

  const memoizedSortedWorkflowSteps = useMemo(
    () =>
      [...quoteWorkflowSteps].sort(
        (a: { position?: number }, b: { position?: number }) =>
          (a.position ?? 0) - (b.position ?? 0),
      ),
    [quoteWorkflowSteps],
  );

  const workflowStatusOptions: WorkflowStatusOptionGroup[] = useMemo(() => {
    const workflowSteps = Object.values(WORKFLOW_STEP_STATUS).map(
      (statusOption) => ({
        label: (
          <StatusSelectorOptionLabel
            icon={statusesToIcon[statusOption]}
            text={WORKFLOW_STEP_STATUS_LABEL[statusOption]}
          />
        ),
        value: statusOption,
      }),
    );

    return [{ options: workflowSteps }];
  }, []);

  if (
    memoizedNotIncludedSteps.length === 0 &&
    memoizedSortedWorkflowSteps.length === 0
  ) {
    return null;
  }

  const totalRows =
    memoizedSortedWorkflowSteps.length + memoizedNotIncludedSteps.length;

  return (
    <TableContainer mt={1}>
      <TableV2 tableLayout="fixed" width="100%">
        <Tbody>
          {memoizedSortedWorkflowSteps.map((step, index) => {
            const rowIndex = index;
            const stepId = step.step.id;
            const label = step.step.name;
            const stepLocator = step.step.locator;
            const status = quoteItemWorkflowSteps?.[stepLocator]?.status;

            return (
              <WorkflowStepRow
                key={stepLocator}
                label={label}
                workStepId={stepId}
                status={status}
                workflowStatusOptions={workflowStatusOptions}
                quoteItemId={quoteItemId}
                isQuoteItemReadOnly={isQuoteItemReadOnly}
                isFirstItemInTable={rowIndex === 0}
                isLastItemInTable={rowIndex === totalRows - 1}
              />
            );
          })}
          {memoizedNotIncludedSteps.map((stepLocator, index) => {
            const rowIndex = memoizedSortedWorkflowSteps.length + index;
            const step = quoteItemWorkflowSteps[stepLocator];
            const label = step.name;
            const status = step.status;

            return (
              <WorkflowStepRow
                key={stepLocator}
                label={label}
                workStepId={null}
                status={status}
                workflowStatusOptions={workflowStatusOptions}
                quoteItemId={quoteItemId}
                isQuoteItemReadOnly={isQuoteItemReadOnly}
                isFirstItemInTable={rowIndex === 0}
                isLastItemInTable={rowIndex === totalRows - 1}
              />
            );
          })}
        </Tbody>
      </TableV2>
    </TableContainer>
  );
};

export default CustomWorkflowSteps;
